import { ChevronLeft, ChevronRight } from 'lucide-react';
import React, { useMemo } from 'react';

type Props = {
  value: string; // YYYY-MM
  onChange: (next: string) => void;
  minYear?: number;
  maxYear?: number;
};

const pad2 = (n: number) => String(n).padStart(2, '0');

const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

const parseYM = (value: string) => {
  const m = /^(\d{4})-(\d{2})$/.exec(value || '');
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return null;
  return { year, month };
};

const monthLabels = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export const MonthYearPicker: React.FC<Props> = ({ value, onChange, minYear, maxYear }) => {
  const now = new Date();
  const defaultMinYear = minYear ?? 2018;
  const defaultMaxYear = maxYear ?? now.getFullYear() + 1;

  const ym = parseYM(value) ?? { year: now.getFullYear(), month: now.getMonth() + 1 };

  const years = useMemo(() => {
    const arr: number[] = [];
    for (let y = defaultMaxYear; y >= defaultMinYear; y--) arr.push(y);
    return arr;
  }, [defaultMinYear, defaultMaxYear]);

  const setYear = (year: number) => {
    const next = `${year}-${pad2(ym.month)}`;
    onChange(next);
  };

  const setMonth = (month: number) => {
    const next = `${ym.year}-${pad2(month)}`;
    onChange(next);
  };

  const shiftMonth = (delta: number) => {
    const d = new Date(ym.year, ym.month - 1 + delta, 1);
    const year = clamp(d.getFullYear(), defaultMinYear, defaultMaxYear);
    const month = d.getMonth() + 1;
    onChange(`${year}-${pad2(month)}`);
  };

  return (
    <div className="flex flex-wrap items-center gap-3">
      <button
        type="button"
        onClick={() => shiftMonth(-1)}
        className="h-10 w-10 shrink-0 flex items-center justify-center rounded-lg border border-gray-300 bg-white hover:bg-gray-50"
        aria-label="Previous month"
      >
        <ChevronLeft className="h-4 w-4 text-gray-700" />
      </button>

      <select
        className="h-10 min-w-[5.5rem] px-3 rounded-lg border border-gray-300 bg-white text-sm font-semibold"
        value={ym.month}
        onChange={e => setMonth(Number(e.target.value))}
      >
        {monthLabels.map((label, idx) => (
          <option key={label} value={idx + 1}>
            {label}
          </option>
        ))}
      </select>

      <select
        className="h-10 min-w-[5.5rem] px-3 rounded-lg border border-gray-300 bg-white text-sm font-semibold"
        value={ym.year}
        onChange={e => setYear(Number(e.target.value))}
      >
        {years.map(y => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>

      <button
        type="button"
        onClick={() => shiftMonth(1)}
        className="h-10 w-10 shrink-0 flex items-center justify-center rounded-lg border border-gray-300 bg-white hover:bg-gray-50"
        aria-label="Next month"
      >
        <ChevronRight className="h-4 w-4 text-gray-700" />
      </button>
    </div>
  );
};
